/** @format **/

import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import PlatformMsisdnPoolNumber from '../../../interfaces/platform/PlatformMsisdnPoolNumber';
import { AcqOperatorsCollectionInterface } from '../../../interfaces/collections/AcqOperatorsCollectionInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { AcqOperatorLoader } from '../../../modules/mnp/AcqOperatorLoader';
import { AcqOperator } from '../../../interfaces/platform/AcqOperator';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import PaperedTable from '../../../components/Table/PaperedTable';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { MnpEventType } from '../../Mnp/MnpEventType';
import { MsisdnMnpHistoryItem } from './MsisdnMnpHistoryItem';
import { StorageReducerAction } from '../../../modules/storage/StorageReducers';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import CardBody from '../../../components/Card/CardBody';
import OplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';

type MsisdnMnpHistoryType = {
    classes: any;
    msisdn: PlatformMsisdnPoolNumber;
};

const defaultFiters = (msisdn: PlatformMsisdnPoolNumber): Array<Filter> => [
    { name: 'msisdn', value: msisdn.msisdn },
    { name: 'eventTypeId', value: '1,2,3,4' },
    { name: 'npType', value: 'MNP' },
];

const defaultOrders = (): Array<Order> => [{ name: 'searchEventDateTime', value: 'desc' }];

export const MsisdnMnpHistory: FunctionComponent<MsisdnMnpHistoryType> = ({ classes, msisdn }): React.ReactElement => {
    const [acqOperators, setAcqOperators] = useState<Array<AcqOperator> | null>(null);
    const [acqHeaders, setAcqHeaders] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>(defaultFiters(msisdn));
    const [orders, setOrders] = useState<Array<Order>>(defaultOrders());
    /** @TODO: Check condition where state below is used. This is workaround for react refresh loop **/
    const [afterFirstLoad, setAfterFirstLoad] = useState<boolean>(false);
    const dispatch = useDispatch();

    const itemLoader = new ItemLoaders(dispatch);
    const acqOperatorsLoaders = new AcqOperatorLoader(itemLoader);
    const mnpEventType = new MnpEventType();

    const tableStickyHeader = {root: classes.tableStickyHeader};

    useEffect(() => {
        const loadAcqOperators = (
            acqOperatorsLoaders: AcqOperatorLoader,
            setAcqOperators: (acqOperators: Array<AcqOperator>) => void,
            setAcqHeaders: (acqHeaders: Array<HeaderCheckBoxSelectListElement>) => void
        ): AcqOperatorsCollectionInterface | null => {
            acqOperatorsLoaders.loadAcqOperators({
                callback: () => {
                    setAcqOperators(acqOperatorsLoaders.getAcqOperators());
                    setAcqHeaders(acqOperatorsLoaders.getAcqOperatorsHeaderList());
                },
            });
            return null;
        };
        if (acqOperators === null && !afterFirstLoad) {
            setAfterFirstLoad(true);
            loadAcqOperators(acqOperatorsLoaders, setAcqOperators, setAcqHeaders);
            dispatch({
                type: StorageReducerAction.LOAD_STORAGE_HISTORY_MNP_LIST,
                plicbd2collection: null,
            });
        }
    }, [dispatch, filters, orders, acqHeaders, acqOperators, acqOperatorsLoaders, afterFirstLoad]);

    const firstLoad = (): void => {};

    const updateOrderList = (newOrders: Array<Order>): void => setOrders(newOrders);
    const updateFilterList = (newFilters: Array<Filter>): void => setFilters(newFilters);
    const updateOrderListRecipient = (newOrders: Array<Order>): void => setOrders(newOrders);
    const updateFilterAcqOperatorListRecipient = (): void => setFilters(acqOperatorsLoaders.updateFilterRptSearch(filters, 'recipient'));
    const updateFilterAcqOperatorListDonor = (): void => setFilters(acqOperatorsLoaders.updateFilterRptSearch(filters, 'donor'));
    const updateFilterOnEmtyList = (fieldName: string): string | Array<number> | Array<string> | Array<boolean> => {
        if (fieldName === 'eventTypeId') {
            return [1, 2, 3, 4];
        }
        return '';
    };

    return (
        <Card>
            <CardHeader trans="pools:msisdn.mnp_history" />
            <CardBody>
                <PaperedTable>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} padding="none" align="left" style={{ paddingLeft: 0 }}>
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="mnp_cases:h.mnpcaseid"
                                    fieldName="caseId"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    setStringToUpper={true}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="mnp_cases:h.case_event_type"
                                    fieldName="eventTypeId"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={mnpEventType.getEventTypes()}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    updateFilterOnEmtyList={updateFilterOnEmtyList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="mnp_cases:h.port_in_date"
                                    fieldName="searchEventDateTime"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="mnp_cases:h.donor"
                                    fieldName="donor"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={acqHeaders}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderListRecipient}
                                    updateFilterList={updateFilterAcqOperatorListDonor}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="mnp_cases:h.recipient"
                                    fieldName="recipient"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={acqHeaders}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderListRecipient}
                                    updateFilterList={updateFilterAcqOperatorListRecipient}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <MsisdnMnpHistoryItem msisdn={msisdn} filters={filters} orders={orders} acqOperators={acqOperatorsLoaders} />
                </PaperedTable>
            </CardBody>
        </Card>
    );
};

export default withStyles(OplAppStyle)(MsisdnMnpHistory);
